// SuperAdminOrganizations.jsx  — versión con creación de tema por palabras clave
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { Button } from 'components/ui/button';
import { toast } from 'sonner';
import {
  Building2, Users, BookOpen, CheckCircle, XCircle,
  Plus, ToggleLeft, ToggleRight, Palette, Sparkles, X,
} from 'lucide-react';

// ── Mini-preview de tema generado ────────────────────────────────────────────
const ThemePreview = ({ theme }) => {
  if (!theme || !theme.accent_primary) return null;
  return (
    <div
      style={{
        marginTop: 12,
        padding: '10px 14px',
        borderRadius: 10,
        background: theme.bg || '#060810',
        border: `1px solid ${theme.accent_primary}55`,
        boxShadow: `0 0 16px ${theme.accent_primary}33`,
      }}
    >
      <p style={{ fontSize: 11, color: theme.accent_primary, fontWeight: 700, marginBottom: 6, letterSpacing: '0.1em' }}>
        ✨ {theme.name || 'Tema generado'}
      </p>
      <p style={{ fontSize: 11, color: theme.text_color || '#fff', opacity: 0.7 }}>
        {theme.description || ''}
      </p>
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        {[theme.accent_primary, theme.accent_secondary, theme.accent_tertiary].map((c, i) =>
          c ? (
            <div key={i} style={{
              width: 22, height: 22, borderRadius: 6,
              background: c,
              boxShadow: `0 0 8px ${c}88`,
              border: '1px solid rgba(255,255,255,0.2)',
            }} title={c} />
          ) : null
        )}
      </div>
    </div>
  );
};

// ── Componente principal ─────────────────────────────────────────────────────
const SuperAdminOrganizations = () => {
  const { token, api } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [generatingTheme, setGeneratingTheme] = useState(false);
  const [generatedTheme, setGeneratedTheme]   = useState(null);
  const [keywordInput, setKeywordInput]       = useState('');

  const [newOrg, setNewOrg] = useState({
    name: '',
    description: '',
    logo_url: '',
    contact_email: '',
    max_users: 100,
    theme_keywords: [],   // ← palabras clave para la IA
  });

  // ── Carga de organizaciones ──────────────────────────────────────────────
  const loadOrganizations = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/superadmin/organizations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      setOrganizations(data);
    } catch {
      toast.error('Error al cargar organizaciones');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadOrganizations(); }, [loadOrganizations]);

  // ── Toggle activo/inactivo ───────────────────────────────────────────────
  const toggleOrganization = async (orgId) => {
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/superadmin/organizations/${orgId}/toggle`,
        { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Estado de la organización actualizado');
      loadOrganizations();
    } catch {
      toast.error('Error al cambiar el estado');
    }
  };

  // ── Añadir/quitar keyword ────────────────────────────────────────────────
  const addKeyword = () => {
    const word = keywordInput.trim().toLowerCase();
    if (!word) return;
    if (newOrg.theme_keywords.length >= 3) {
      toast.info('Máximo 3 palabras clave');
      return;
    }
    if (newOrg.theme_keywords.includes(word)) {
      toast.info('Esa palabra ya está añadida');
      return;
    }
    setNewOrg(prev => ({ ...prev, theme_keywords: [...prev.theme_keywords, word] }));
    setKeywordInput('');
    setGeneratedTheme(null); // resetear preview
  };

  const removeKeyword = (word) => {
    setNewOrg(prev => ({
      ...prev,
      theme_keywords: prev.theme_keywords.filter(w => w !== word),
    }));
    setGeneratedTheme(null);
  };

  // ── Preview: llamar a la IA solo para ver cómo quedaría ─────────────────
  const previewTheme = async () => {
    if (newOrg.theme_keywords.length === 0) {
      toast.info('Añade al menos 1 palabra clave');
      return;
    }
    setGeneratingTheme(true);
    try {
      // Usamos el endpoint de crear con theme_keywords solo para preview:
      // En su lugar pedimos al backend que genere el tema sin crear la org.
      // Como no existe un endpoint de preview dedicado, llamamos directamente
      // a un mini-endpoint (ver más abajo en server.py) o hacemos la petición
      // con un flag. Aquí llamamos al endpoint de preview si lo tienes,
      // si no, se lo mostramos al crear.
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/superadmin/preview-theme`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ keywords: newOrg.theme_keywords }),
        }
      );
      if (res.ok) {
        const theme = await res.json();
        setGeneratedTheme(theme);
      } else {
        toast.error('No se pudo previsualizar el tema');
      }
    } catch {
      toast.error('Error al generar la previsualización');
    } finally {
      setGeneratingTheme(false);
    }
  };

  // ── Crear organización ───────────────────────────────────────────────────
  const createOrganization = async () => {
    if (!newOrg.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    try {
      const payload = {
        ...newOrg,
        // Si ya generamos un preview, lo mandamos para evitar doble llamada IA
        theme: generatedTheme || undefined,
      };
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organizations`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        }
      );
      if (response.ok) {
        toast.success('Organización creada con su tema personalizado ✨');
        setShowCreateModal(false);
        resetModal();
        loadOrganizations();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Error al crear organización');
      }
    } catch {
      toast.error('Error al crear organización');
    }
  };

  const resetModal = () => {
    setNewOrg({ name: '', description: '', logo_url: '', contact_email: '', max_users: 100, theme_keywords: [] });
    setKeywordInput('');
    setGeneratedTheme(null);
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Gestión de Organizaciones</h1>
          <p className="text-muted-foreground">Administra todas las organizaciones de la plataforma</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" /> Nueva Organización
        </Button>
      </div>

      {/* Grid de organizaciones */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {organizations.map((org) => (
          <div
            key={org.id}
            className={`bg-card rounded-lg border p-6 ${!org.active ? 'opacity-60' : ''}`}
            style={org.theme?.accent_primary ? {
              borderColor: `${org.theme.accent_primary}44`,
              boxShadow:   `0 0 16px ${org.theme.accent_primary}22`,
            } : {}}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                {/* Icono con color del tema */}
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{
                    background: org.theme?.card_bg || 'rgba(99,102,241,0.1)',
                    border: `1px solid ${org.theme?.accent_primary || '#6366f1'}44`,
                  }}
                >
                  <Building2 className="w-6 h-6" style={{ color: org.theme?.accent_primary || '#6366f1' }} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{org.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {org.active ? (
                      <span className="flex items-center gap-1 text-green-500">
                        <CheckCircle className="w-3 h-3" /> Activa
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-500">
                        <XCircle className="w-3 h-3" /> Inactiva
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => toggleOrganization(org.id)}>
                {org.active
                  ? <ToggleRight className="w-5 h-5 text-green-500" />
                  : <ToggleLeft  className="w-5 h-5 text-gray-400" />}
              </Button>
            </div>

            <p className="text-sm text-muted-foreground mb-4">{org.description || 'Sin descripción'}</p>

            {/* Tema de la org */}
            {org.theme?.name && (
              <div
                className="mb-4 px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-2"
                style={{
                  background: org.theme.badge_bg || 'rgba(99,102,241,0.1)',
                  color:      org.theme.badge_color || org.theme.accent_primary || '#6366f1',
                  border:     `1px solid ${org.theme.accent_primary || '#6366f1'}33`,
                }}
              >
                <Palette className="w-3 h-3" />
                {org.theme.name}
                {org.theme_keywords?.length > 0 && (
                  <span className="opacity-60 font-normal">
                    · {org.theme_keywords.join(', ')}
                  </span>
                )}
              </div>
            )}

            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2"><Users className="w-4 h-4" />Usuarios</span>
                <span className="font-semibold">{org.current_users} / {org.max_users}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2"><BookOpen className="w-4 h-4" />Módulos</span>
                <span className="font-semibold">{org.total_modules || 0}</span>
              </div>
            </div>

            {org.contact_email && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-muted-foreground">📧 {org.contact_email}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Modal de Creación ──────────────────────────────────────────────── */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl max-w-md w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-5">Nueva Organización</h2>

            <div className="space-y-4">
              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium mb-1">Nombre *</label>
                <input
                  type="text"
                  value={newOrg.name}
                  onChange={e => setNewOrg({ ...newOrg, name: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border bg-background text-sm"
                  placeholder="Nombre de la organización"
                />
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-medium mb-1">Descripción</label>
                <textarea
                  value={newOrg.description}
                  onChange={e => setNewOrg({ ...newOrg, description: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border bg-background text-sm"
                  rows={2}
                  placeholder="Descripción breve"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-1">Email de contacto</label>
                <input
                  type="email"
                  value={newOrg.contact_email}
                  onChange={e => setNewOrg({ ...newOrg, contact_email: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border bg-background text-sm"
                  placeholder="contacto@empresa.com"
                />
              </div>
              <div>
  <label className="block text-sm font-medium mb-1">URL del logo</label>
  <input
    type="url"
    value={newOrg.logo_url}
    onChange={e => setNewOrg({ ...newOrg, logo_url: e.target.value })}
    className="w-full px-4 py-2 rounded-lg border bg-background text-sm"
    placeholder="https://miempresa.com/logo.png"
  />
  {newOrg.logo_url && (
    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
      <img
        src={newOrg.logo_url}
        alt="Preview del logo"
        style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 6, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
        onError={e => { e.target.style.display = 'none'; }}
      />
      <span style={{ fontSize: 11, color: 'var(--color-text-secondary, #999)' }}>Vista previa</span>
    </div>
  )}
</div>


              {/* Máx. usuarios */}
              <div>
                <label className="block text-sm font-medium mb-1">Máximo de usuarios</label>
                <input
                  type="number"
                  value={newOrg.max_users}
                  onChange={e => setNewOrg({ ...newOrg, max_users: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 rounded-lg border bg-background text-sm"
                  min="1"
                />
              </div>

              {/* ── SECCIÓN DE TEMA ─────────────────────────────────────── */}
              <div className="pt-2 border-t">
                <div className="flex items-center gap-2 mb-3">
                  <Palette className="w-4 h-4 text-violet-400" />
                  <span className="text-sm font-semibold">Tema visual (IA)</span>
                  <span className="text-xs text-muted-foreground ml-auto">máx. 3 palabras</span>
                </div>

                <p className="text-xs text-muted-foreground mb-3">
                  Escribe palabras que describan la empresa (industria, valores, ambiente).
                  La IA generará una estética única para sus usuarios.
                </p>

                {/* Input de keywords */}
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={keywordInput}
                    onChange={e => setKeywordInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); }}}
                    className="flex-1 px-3 py-2 rounded-lg border bg-background text-sm"
                    placeholder='Ej: "oceano", "tecnología", "energía"'
                    disabled={newOrg.theme_keywords.length >= 3}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={addKeyword}
                    disabled={newOrg.theme_keywords.length >= 3 || !keywordInput.trim()}
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>

                {/* Chips de keywords */}
                {newOrg.theme_keywords.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {newOrg.theme_keywords.map(word => (
                      <span
                        key={word}
                        className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold"
                        style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.35)', color: '#a78bfa' }}
                      >
                        {word}
                        <button onClick={() => removeKeyword(word)} className="hover:opacity-70">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Botón previsualizar */}
                {newOrg.theme_keywords.length > 0 && (
                  <button
                    onClick={previewTheme}
                    disabled={generatingTheme}
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-all"
                    style={{
                      background: generatingTheme ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.2)',
                      border: '1px solid rgba(99,102,241,0.4)',
                      color: '#a78bfa',
                      cursor: generatingTheme ? 'wait' : 'pointer',
                    }}
                  >
                    <Sparkles className={`w-4 h-4 ${generatingTheme ? 'animate-spin' : ''}`} />
                    {generatingTheme ? 'Generando tema…' : 'Previsualizar tema'}
                  </button>
                )}

                {/* Preview del tema generado */}
                {generatedTheme && <ThemePreview theme={generatedTheme} />}

                {/* Nota: si no pones keywords, se usa cyberpunk por defecto */}
                {newOrg.theme_keywords.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-2 opacity-60">
                    Sin palabras clave → se usará la estética Cyberpunk base
                  </p>
                )}
              </div>
            </div>

            {/* Acciones */}
            <div className="flex gap-2 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowCreateModal(false); resetModal(); }}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1"
                onClick={createOrganization}
                disabled={!newOrg.name}
              >
                {newOrg.theme_keywords.length > 0 ? '✨ Crear con Tema' : 'Crear'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminOrganizations;